/**
 * v3.approvals — WAVE-9 Microsoft Graph Approvals client.
 *
 * Backed by Functions/ApprovalsFunction.cs. Surfaces pending Teams
 * Approvals inline in v3's Approvals tab so FSRs/supervisors don't need
 * to switch to the Teams Approvals app.
 */

import type {
  RequestOptions,
  ApprovalSummary,
  ApprovalDecisionInput,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface ApprovalsListResponse {
  success: boolean;
  count: number;
  approvals: ReadonlyArray<ApprovalSummary>;
}

export interface ApprovalsDecisionResponse {
  success: boolean;
  approvalId?: string;
  decision?: 'approve' | 'reject';
  error?: string;
}

export class ApprovalsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(opts?: RequestOptions): Promise<ApprovalsListResponse> {
    return this.http.get<ApprovalsListResponse>('/v3/approvals', { ...opts });
  }

  public history(
    filters: { from?: string; to?: string; top?: number } = {},
    opts?: RequestOptions,
  ): Promise<ApprovalsListResponse> {
    return this.http.get<ApprovalsListResponse>('/v3/approvals/history', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public approve(
    approvalId: string,
    body: ApprovalDecisionInput,
    opts?: RequestOptions,
  ): Promise<ApprovalsDecisionResponse> {
    return this.http.post<ApprovalsDecisionResponse>(
      `/v3/approvals/${encodeURIComponent(approvalId)}/approve`,
      { ...opts, body },
    );
  }

  public reject(
    approvalId: string,
    body: ApprovalDecisionInput,
    opts?: RequestOptions,
  ): Promise<ApprovalsDecisionResponse> {
    return this.http.post<ApprovalsDecisionResponse>(
      `/v3/approvals/${encodeURIComponent(approvalId)}/reject`,
      { ...opts, body },
    );
  }
}
